import { createSelector } from '@reduxjs/toolkit'
import { RootState } from 'app/store'
import getDataSelector from 'core/utils/getDataSelector'
import { createSharedSelector, selectParamsFromProps } from 'core/utils/selectorHelpers'
import DataKeys from 'k8s/DataKeys'
import { defaultAppIconPath } from '../app-catalog/constant'
import { calculateResourceTotals, convertMBToGB } from './helper'

function sortByCreatedAtDesc<T extends { created_at?: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0
    return dateB - dateA
  })
}

export const repositorySelector = createSharedSelector(
  getDataSelector<DataKeys.Repository>(DataKeys.Repository),
  (repositories) => {
    return repositories?.map((repository) => {
      const name = repository?.url || '-'
      const description = repository?.description || '-'
      const keyName = repository?.name || '-'
      const keyType = repository?.auth?.key_type || '-'
      return {
        ...repository,
        name,
        description,
        keyName,
        keyType,
      }
    })
  },
)

export const branchSelector = createSharedSelector(
  getDataSelector<DataKeys.RepositoryBranches>(DataKeys.RepositoryBranches),
  (branches) => {
    return branches?.map((branch) => ({
      label: branch?.name,
      value: branch?.name,
    }))
  },
)

export const templateSelector = createSharedSelector(
  getDataSelector<DataKeys.Templates>(DataKeys.Templates),
  (templates) => {
    const sortedTemplates = sortByCreatedAtDesc(templates)
    return sortedTemplates?.map((template) => {
      const iconName = template?.icon ? template?.icon : defaultAppIconPath
      const headerName = template?.name || '-'
      const subHeaderName = template?.description || '-'
      const version = template?.repo_source?.branch || '-'
      const ownerName = template?.owner || '-'

      // Sum up RAM, vCPUs, and Disk
      const { totalRam, totalVcpus, totalDisk } = calculateResourceTotals(template?.resources || [])

      const vms = template?.resources?.length

      return {
        ...template,
        iconName,
        headerName,
        subHeaderName,
        vms,
        totalRam: convertMBToGB(totalRam),
        totalVcpus,
        totalDisk,
        version,
        ownerName,
      }
    })
  },
)

export const allAppSelector = createSharedSelector(
  getDataSelector<DataKeys.Applications>(DataKeys.Applications),
  getDataSelector<DataKeys.Templates>(DataKeys.Templates),
  (allApps, templates) => {
    // Sort by created_at descending
    const sortedApps = sortByCreatedAtDesc(allApps)
    return sortedApps?.map((allApp) => {
      const headerName = allApp?.name || '-'
      const subHeaderName = allApp?.description || '-'
      const status = allApp?.status
      const version = allApp?.repo_source?.branch || '-'
      const matchingTemplate = templates.find((template) => template?.uid === allApp?.template_id)
      const templateName = matchingTemplate?.name || '-'
      const iconName = matchingTemplate?.icon ? matchingTemplate?.icon : defaultAppIconPath
      const ownerName = allApp?.owner || '-'

      // Sum up RAM, vCPUs, and Disk
      const { totalRam, totalVcpus, totalDisk } = calculateResourceTotals(allApp?.resources || [])

      const vms = allApp?.resources?.length

      return {
        ...allApp,
        iconName,
        headerName,
        subHeaderName,
        status,
        vms,
        totalRam: convertMBToGB(totalRam),
        totalVcpus,
        totalDisk,
        version,
        templateName,
        ownerName,
      }
    })
  },
)

export const myAppSelector = createSharedSelector(
  allAppSelector,
  (state: RootState) => state?.session?.userDetails,
  (myApps, user) => {
    return myApps?.filter((app) => app?.owner === user?.email)
  },
)

export const makeGetVMIdsByAppSelector = (
  defaultParams: { appId?: string } = {
    appId: '',
  },
) => {
  const selectParams = selectParamsFromProps(defaultParams)
  return createSelector(allAppSelector, selectParams, (apps, params) => {
    const app = apps?.find((app) => app?.uid === params?.appId)
    return app?.resources?.map((resource) => resource?.id) || []
  })
}

export const appDetailsSelector = createSharedSelector(
  getDataSelector<DataKeys.ApplicationDetails>(DataKeys.ApplicationDetails),
  (output) => {
    return output
  },
)
